import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getClinicSubscriptionStatus } from '../../Services/APIs/appDetails';
import { getFlowDecision } from '../../Utils/clinicFunnel';
import BillingLayout from './BillingLayout';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30000;

const BUTTON_CLASSES = "w-full h-12 bg-claraview-secondary hover:brightness-110 active:brightness-95 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase";
const LINK_CLASSES = "mt-3.5 w-full text-center font-spaceMono text-[10px] tracking-[0.8px] uppercase underline underline-offset-2 cv2-bill-reassurance";

const SubscriptionPending = () => {
    const navigate = useNavigate();
    const [timedOut, setTimedOut] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        let intervalId;
        let timeoutId;

        const stopPolling = () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };

        const checkStatus = async () => {
            try {
                const response = await getClinicSubscriptionStatus();
                const route = getFlowDecision(response?.data?.data.status, response?.data?.data.subscription_status);
                if (!cancelled && route !== '/pricing') {
                    stopPolling();
                    navigate(route, { replace: true });
                }
            } catch (error) {
                // transient network error - next poll will retry
            }
        };

        checkStatus();
        intervalId = setInterval(checkStatus, POLL_INTERVAL_MS);
        timeoutId = setTimeout(() => {
            if (cancelled) return;
            stopPolling();
            setTimedOut(true);
        }, POLL_TIMEOUT_MS);

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [navigate, attempt]);

    return (
        <BillingLayout
            priceLabel="Price"
            priceCopy="Billed monthly. Cancel anytime."
            footerLabel="Status"
            footerValue="Processing"
            centered
            actions={timedOut ? (
                <>
                    <button
                        type="button"
                        onClick={() => { setTimedOut(false); setAttempt((n) => n + 1); }}
                        className={BUTTON_CLASSES}
                    >
                        Check Again
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/pricing', { replace: true })}
                        className={LINK_CLASSES}
                    >
                        Back to pricing
                    </button>
                </>
            ) : null}
        >
            {!timedOut && (
                <Loader2 className="w-6 h-6 text-claraview-secondary animate-spin mx-auto mb-6" strokeWidth={2} />
            )}

            <p className="cv2-bill-eyebrow font-spaceMono text-[10px] tracking-[1.4px] uppercase mb-4">
                {timedOut ? 'Almost There' : 'Payment Processing'}
            </p>
            <h2 className="font-aeonik font-normal text-[32px] leading-[38px] tracking-[-0.7px] text-cv2-foreground normal-case mb-3">
                {timedOut ? 'Still Confirming' : 'Verifying Your Payment'}
            </h2>
            <p className="cv2-bill-subcopy font-aeonik text-sm leading-[22px] normal-case">
                {timedOut
                    ? "This is taking longer than expected — we'll email you once it's confirmed."
                    : 'Hang tight, this only takes a moment…'}
            </p>
        </BillingLayout>
    );
};

export default SubscriptionPending;
